import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { requestAvalList } from '../../../services/requests';
import { requestInputsChoices } from '../../../services/newNotesRequest';

const emptyAvaliacaoData = {
	acoes_realizadas: null,
	padrao_trabalho: null,
	observacoes: '',
	classificacao_final: '',
	sub_classificacao_final: '',
	tipo_incidente_final: '',
	tipo_incidente_final_outros: '',
};

const formatBooleanLabel = (value) => {
	if (value === true) return 'Sim';
	if (value === false) return 'Não';
	return '';
};

const formatClassificationLabel = (value) => {
	if (!value) return '';
	const noSeparators = String(value).replace(/[_-]+/g, ' ');
	const lower = noSeparators.toLowerCase();
	return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const DisplayField = ({ label, value, className = '' }) => (
	<div className={`flex flex-col gap-1 ${className}`}>
		<p className='text-[#004A80] font-medium'>{label}</p>
		<div className='min-h-[40px] flex items-center px-2 py-2 border-[1px] rounded-md border-[#E3E3E3] whitespace-pre-wrap break-words'>
			{value || 'Não preenchido'}
		</div>
	</div>
);

const avaliacaoListInFlight = new Map();
const inputsChoicesInFlight = new Map();

const getRequestCacheKey = (...parts) =>
	parts.map((part) => String(part || '').trim()).join('|');

const requestAvalListOnce = (token) => {
	const key = getRequestCacheKey('avaliacoes', token);
	if (avaliacaoListInFlight.has(key)) {
		return avaliacaoListInFlight.get(key);
	}

	const requestPromise = requestAvalList(token).finally(() => {
		avaliacaoListInFlight.delete(key);
	});
	avaliacaoListInFlight.set(key, requestPromise);
	return requestPromise;
};

const requestInputsChoicesOnce = () => {
	const key = 'inputs-choices';
	if (inputsChoicesInFlight.has(key)) {
		return inputsChoicesInFlight.get(key);
	}

	const requestPromise = requestInputsChoices().finally(() => {
		inputsChoicesInFlight.delete(key);
	});
	inputsChoicesInFlight.set(key, requestPromise);
	return requestPromise;
};

const loadAvaliacaoData = async (notificacao) => {
	if (!notificacao?.protocolo) return emptyAvaliacaoData;

	const userCookie = Cookies.get('user');
	if (!userCookie) return emptyAvaliacaoData;

	const { token } = JSON.parse(userCookie);
	if (!token) return emptyAvaliacaoData;

	const [avaliacoes, choices] = await Promise.all([
		requestAvalListOnce(token),
		requestInputsChoicesOnce().catch(() => null),
	]);

	if (!Array.isArray(avaliacoes) || avaliacoes.length === 0) {
		return emptyAvaliacaoData;
	}

	const avaliacao = avaliacoes.find(
		(item) => item?.protocolo === notificacao.protocolo,
	);

	if (!avaliacao) return emptyAvaliacaoData;
	let neverEventLabel = avaliacao.sub_classificacao_final || '';
	const subChoices = choices?.sub_classificacao || {};
	if (avaliacao.sub_classificacao_final && subChoices) {
		const entry = Object.entries(subChoices).find(
			([key, item]) =>
				String(key) === String(avaliacao.sub_classificacao_final) ||
				item?.codigo === avaliacao.sub_classificacao_final,
		);
		if (entry?.[1]?.nome) {
			neverEventLabel = entry[1].nome;
		}
	}

	return {
		acoes_realizadas: avaliacao.acoes_realizadas ?? null,
		padrao_trabalho: avaliacao.padrao_trabalho ?? null,
		observacoes: avaliacao.observacoes || '',
		classificacao_final: formatClassificationLabel(
			avaliacao.classificacao_final,
		),
		sub_classificacao_final: neverEventLabel,
		tipo_incidente_final:
			choices?.tipo_incidente[avaliacao.tipo_incidente_final] || '',
		tipo_incidente_final_outros:
			avaliacao.tipo_incidente_final_outros || '',
	};
};

export default function NewAvaliacaoPreenchido({ notificacao }) {
	// Investigadores não devem ver a avaliação final
	const _userCookie = Cookies.get('user');
	const _parsedUser = _userCookie ? JSON.parse(_userCookie) : null;
	const _isInvestigador =
		Array.isArray(_parsedUser?.permissao) &&
		_parsedUser.permissao.includes('Investigadores') &&
		!_parsedUser.permissao.includes('Classificadores');

	const [formData, setFormData] = useState(emptyAvaliacaoData);
	const protocolo = notificacao?.protocolo;
	const loadedAvaliacaoKeyRef = React.useRef('');

	useEffect(() => {
		const handleLoad = async () => {
			try {
				if (!protocolo) return;
				const userCookie = Cookies.get('user');
				const token = userCookie ? JSON.parse(userCookie)?.token : '';
				const loadKey = `${token}|${protocolo}`;
				if (loadedAvaliacaoKeyRef.current === loadKey) return;
				loadedAvaliacaoKeyRef.current = loadKey;

				const result = await loadAvaliacaoData({ protocolo });
				setFormData(result);
			} catch (error) {
				console.error('Erro ao carregar avaliação final:', error);
			}
		};

		handleLoad();
	}, [protocolo]);

	const hasReclassificacao = Boolean(
		formData.classificacao_final ||
		formData.tipo_incidente_final ||
		formData.tipo_incidente_final_outros,
	);

	if (_isInvestigador) return null;

	return (
		<div className='max-w-[1136px] font-inter bg-white'>
			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				<div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					<h2>AVALIAÇÃO FINAL</h2>
				</div>
				<div className='p-3 grid grid-cols-1 md:grid-cols-2 gap-3'>
					<DisplayField
						label='As ações realizadas foram suficientes para bloquear a reincidência do incidente?'
						value={formatBooleanLabel(formData.acoes_realizadas)}
					/>
					<DisplayField
						label='Foi criado ou alterado algum padrão de trabalho para evitar a reincidência do incidente?'
						value={formatBooleanLabel(formData.padrao_trabalho)}
					/>
					<DisplayField
						label='Observações'
						value={formData.observacoes}
						className='md:col-span-2'
					/>
				</div>
			</section>

			<section className='border border-[#E3E3E3] rounded-md mt-[16px]'>
				{/* <div className='text-[#004A80] bg-[#F4F4F4] rounded-t-md text-[18px] text-center font-semibold border-[#E3E3E3] border-b-[1px] py-1'>
					<h2>RECLASSIFICAÇÃO DO INCIDENTE</h2>
				</div> */}
				<div className='p-3 grid grid-cols-1 md:grid-cols-2 gap-3'>
					<DisplayField
						label='Houve correção do tipo ou da classificação do incidente?'
						value={hasReclassificacao ? 'Sim' : 'Não'}
						className='md:col-span-2'
					/>

					{hasReclassificacao && (
						<>
							<DisplayField
								label='Nova classificação'
								value={formData.classificacao_final}
							/>
							{formData.classificacao_final.toUpperCase() ===
								'Never events'.toUpperCase() && (
								<DisplayField
									label='Never Event'
									value={formData.sub_classificacao_final}
								/>
							)}
							<DisplayField
								label='Novo tipo de incidente'
								value={formData.tipo_incidente_final}
							/>

							{formData.tipo_incidente_final === 'OUTROS' && (
								<DisplayField
									label='Outro tipo de incidente'
									value={formData.tipo_incidente_final_outros}
								/>
							)}
						</>
					)}
				</div>
			</section>
		</div>
	);
}
